import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    ViewChild,
    ViewContainerRef,
    ComponentRef,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Type,
    Injector,
    inject,
    ChangeDetectorRef,
    ViewEncapsulation
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TooltipDirective } from '@shared/directives/tooltip';
import { CommonModule } from '@angular/common';

export type RightPanelMenuOption = {
    icon: string;
    name: string;
    route?: string;
    action?: () => void;
    disabled?: boolean;
    tooltip?: string;
};

export type RightPanelContent = {
    title: string;
    icon?: string;
    component?: Type<unknown>;
    data?: Record<string, unknown>;
    injector?: Injector;
    menuOptions?: RightPanelMenuOption[];
    showCloseButton?: boolean;
    showExpandableButton?: boolean;
    showCollapseToggle?: boolean;
};

@UntilDestroy()
@Component({
    selector: 'app-right-panel',
    templateUrl: './right-panel.component.html',
    styleUrl: './right-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, TooltipDirective]
})
export class RightPanelComponent implements OnChanges, OnDestroy {
    @Input() public isVisible = false;
    @Input() public isExpanded = false;
    @Input() public isCollapsed = false;
    @Input() public content: RightPanelContent | null = null;
    @Input() public showCloseButton = true;
    @Input() public showExpandableButton = true;

    @Output() public visibilityChange = new EventEmitter<boolean>();
    @Output() public expandedChange = new EventEmitter<boolean>();
    @Output() public collapsedChange = new EventEmitter<boolean>();

    @ViewChild('dynamicComponentHost', { read: ViewContainerRef, static: true })
    public dynamicComponentHost!: ViewContainerRef;

    private readonly router = inject(Router);
    private readonly cdr = inject(ChangeDetectorRef);
    private componentRef: ComponentRef<unknown> | null = null;

    public constructor() {
        // Auto-reset panel state when navigating to a new route
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                untilDestroyed(this)
            )
            .subscribe(() => {
                // Hide the panel if visible (service will handle state reset)
                if (this.isVisible) {
                    this.close();
                }

                this.cdr.markForCheck();
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['content'] && this.content) {
            if (this.content.showCloseButton !== undefined) {
                this.showCloseButton = this.content.showCloseButton;
            }
            // Update showExpandableButton based on content if provided
            if (this.content.showExpandableButton !== undefined) {
                this.showExpandableButton = this.content.showExpandableButton;
            }
            this.loadDynamicComponent();
        }

        this.cdr.markForCheck();
    }

    public ngOnDestroy(): void {
        this.clearComponent();
    }

    public close() {
        this.isVisible = false;
        // Only emit visibility change - let the service handle state resets without emissions
        this.visibilityChange.emit(false);

        // Don't emit state changes - just update locally
        // The service will reset via two-way binding after hide() is called
        this.isExpanded = false;
        this.isCollapsed = false;

        this.clearComponent();
        this.cdr.markForCheck();
    }

    public toggleExpanded() {
        this.isExpanded = !this.isExpanded;
        this.expandedChange.emit(this.isExpanded);
        // If expanding, make sure we're not collapsed
        if (this.isExpanded && this.isCollapsed) {
            this.isCollapsed = false;
            this.collapsedChange.emit(false);
        }
        this.cdr.markForCheck();
    }

    public toggleCollapsed() {
        this.isCollapsed = !this.isCollapsed;
        this.collapsedChange.emit(this.isCollapsed);
        // If collapsing, make sure we're not expanded
        if (this.isCollapsed && this.isExpanded) {
            this.isExpanded = false;
            this.expandedChange.emit(false);
        }
        this.cdr.markForCheck();
    }

    public showRightMenu() {
        return (
            this.menuOptionsWithCollapseToggle &&
            (this.menuOptionsWithCollapseToggle.length > 1 ||
                (this.menuOptionsWithCollapseToggle.length > 0 && this.isCollapsed))
        );
    }
    public get menuOptionsWithCollapseToggle(): RightPanelMenuOption[] {
        const menuOptions = this.content?.menuOptions || [];
        const shouldShowCollapseToggle = this.content?.showCollapseToggle ?? true; // Default to true for backward compatibility

        // If collapse toggle should not be shown, return only the menu options
        if (!shouldShowCollapseToggle) {
            return menuOptions;
        }

        // Only show the "show" button in the menu when panel is collapsed
        // The "hide" button is now in the top panel controls
        // if (this.isCollapsed) {
        //     const showToggle: RightPanelMenuOption = {
        //         icon: 'keyboard_double_arrow_left',
        //         name: 'Show content',
        //         action: () => this.toggleCollapsed(),
        //         tooltip: 'Show content'
        //     };

        //     // Return show toggle as the first item, followed by other options
        //     return [showToggle, ...menuOptions];
        // }

        // When not collapsed, return only the regular menu options (hide button is in top panel)
        return menuOptions;
    }

    public handleMenuAction(option: RightPanelMenuOption) {
        if (option.disabled) {
            return;
        }

        if (option.action) {
            // Execute custom action
            //if (this.isCollapsed) this.toggleCollapsed();
            option.action();
        } else if (option.route) {
            // Navigate to route
            void this.router.navigate([option.route]);
        }
    }

    private loadDynamicComponent(): void {
        this.clearComponent();

        if (this.content?.component) {
            // Dynamic component loading
            const componentType = this.content.component;
            this.componentRef = this.dynamicComponentHost.createComponent(componentType, {
                injector: this.content.injector
            });

            // Pass data to the component by setting individual properties
            const instance = this.componentRef.instance as Record<string, unknown>;
            if (instance && this.content.data) {
                // Use setInput method which is more reliable for dynamic components
                Object.keys(this.content.data).forEach((key) => {
                    if (this.content?.data) {
                        try {
                            this.componentRef?.setInput(key, this.content.data[key]);
                        } catch {
                            // Fallback to direct property assignment
                            if (key in instance) {
                                instance[key] = this.content.data[key];
                            }
                        }
                    }
                });

                // Trigger change detection
                if (this.componentRef?.changeDetectorRef) {
                    this.componentRef.changeDetectorRef.detectChanges();
                }
            }
        }
    }

    private clearComponent(): void {
        if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
        this.dynamicComponentHost?.clear();
    }
}
